import buildStateMemoryMiddleware from "@builder/store/slices/BuilderSlice/buildStateMemory";
import builderVisibilitySlice from "@builder/store/slices/BuilderSlice/builderVisibilitySlice";
import scaleTestSlice from "@builder/store/slices/frameTransformSlice";
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import builderSlice from "./slices/BuilderSlice/builderSlice";
import actionBuilderSlice from "./slices/actionBuilderSlice";
import systemUISlice from "./slices/systemUISlice";
import popupSlice from "./slices/popupSlice";
import sidebarSlice from "./slices/sidebarSlice";
import { buildSettingsKeyApi } from "@builder/store/slices/buildSettingsKeys/buildSettingsKeyApi";
import buildSettingsKeySlice from "@builder/store/slices/buildSettingsKeys/buildSettingsKeySlice";
import { extensionsApi } from "./slices/extensionsApiSlice/extensionsApiSlice";
import { companyExtensionsSlice } from "./slices/extensionsApiSlice/companyExtensionApiSlice";
import { buildsApi } from "./slices/BuildsApiSlice/buildsApiSlice";
import storeDataSlice from "./slices/storeDataSlice";
import { categoriesApi } from "./slices/dataApiSlices/categoriesApiSlice";
import { customersApi } from "./slices/dataApiSlices/customersApiSlice";
import { rolesApi } from "./slices/dataApiSlices/rolesApiSlice";
import { productsApi } from "./slices/dataApiSlices/productsApiSlice";
import { ordersApi } from "./slices/dataApiSlices/ordersApiSlice";
import { usersApi } from "./slices/dataApiSlices/userApiSlice";
import { builderElementsInstalledApiSlice } from "./slices/builderElementsInstalledApiSlice";
import { companiesApi } from "./slices/dataApiSlices/companiesApiSlice";
import { stripeApi } from "./slices/dataApiSlices/stripeApiSlice";
import { attachmentsApi } from "./slices/attachmentsApiSlice/attachmentsApiSlice";
import { buildSettingsApi } from "./slices/buildSettings/buildSettingsApi";
import { zapierApi } from "./slices/zapier/zapierApiSlice";
import storeDataMiddleware from "./middleware/storeData";
import { taxApi } from "./slices/dataApiSlices/taxApiSlice";
import { planApi } from "./slices/dataApiSlices/planApiSlice";

const rootReducer = combineReducers({
    builder: builderSlice,
    sidebar: sidebarSlice,
    popup: popupSlice,
    frameTransform: scaleTestSlice,
    visibility: builderVisibilitySlice,
    actionBuilder: actionBuilderSlice,
    attachmentsApi: attachmentsApi.reducer,
    systemUI: systemUISlice,
    buildSettingsKey: buildSettingsKeySlice,
    buildSettingsKeyApi: buildSettingsKeyApi.reducer,
    storeData: storeDataSlice,
    extensions: extensionsApi.reducer,
    companyExtensions: companyExtensionsSlice.reducer,
    buildsApi: buildsApi.reducer,
    categoriesApi: categoriesApi.reducer,
    customersApi: customersApi.reducer,
    ordersApi: ordersApi.reducer,
    productsApi: productsApi.reducer,
    builderElementsInstalledApi: builderElementsInstalledApiSlice.reducer,
    rolesApi: rolesApi.reducer,
    usersApi: usersApi.reducer,
    companiesApi: companiesApi.reducer,
    stripeApi: stripeApi.reducer,
    buildSettingsApi: buildSettingsApi.reducer,
    zapierApi: zapierApi.reducer,
    taxApi: taxApi.reducer,
    planApi: planApi.reducer
});

const store = configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({ serializableCheck: false }).concat(
            buildStateMemoryMiddleware,
            buildSettingsKeyApi.middleware,
            extensionsApi.middleware,
            companyExtensionsSlice.middleware,
            attachmentsApi.middleware,
            buildsApi.middleware,
            categoriesApi.middleware,
            customersApi.middleware,
            ordersApi.middleware,
            productsApi.middleware,
            builderElementsInstalledApiSlice.middleware,
            rolesApi.middleware,
            usersApi.middleware,
            companiesApi.middleware,
            stripeApi.middleware,
            buildSettingsApi.middleware,
            zapierApi.middleware,
            storeDataMiddleware,
            taxApi.middleware,
            planApi.middleware
        )
});

export default store;
export const useAppSelector: TypedUseSelectorHook<ReturnType<typeof store.getState>> = useSelector;
export const useAppDispatch = () => useDispatch<typeof store.dispatch>();
export type RootState = ReturnType<typeof store.getState>;
